import { Box, Grid } from "@mui/material";
import { MprcChipChoice, MprcIconButtonDropdown, MprcText } from "components";
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterIconButton, MprcFilterLine } from "components/filter";
import { IconSort } from "components/icons";
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { CoreFilterCommand, FeedAuthorSelectionType, FeedType, KnowledgeFeedOrder, KnowledgeQuickFilterType, QuickFilterDto, StickerAppliesTo, UserProfileDto } from "generated/CoreApi_v1";
import { TFunction } from "i18next";
import { FEED_EXTERNAL_FILTER_ID } from "layouts/common/inside/MprcLayoutInside";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdvancedFilter, IAdvFilterCommand, IAdvFilterDropdown, IAdvFilterMenuItem } from "views/general/AdvancedFilter";
import { changeQuickFilter, clearAdvFilter, getClearFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { containsMenuOptions, IFeedFilterOptions, orderMenuOptions, othersChannelMenuOptions, periodMenuOptions, sourceAuthorMenuOptions, stickersMenuOptions } from "../components/FeedFilter";
import { QuickFilter } from "../components/FeedFilter/QuickFilter";

////////////////////////////////////////////////////////////////////////////

function getDropdownOptions(t: TFunction, stickers: IAdvFilterCommand[], canFilterAuthor: boolean) {
    return [
        sourceAuthorMenuOptions(t, canFilterAuthor),
        containsMenuOptions(t),
        periodMenuOptions(t),
        stickersMenuOptions(t, stickers),
        othersChannelMenuOptions(t, true)
    ];
}

//

interface IProps {
    count?: number;
    filter: IFeedFilterOptions;
    expandedAdvFilter: boolean;
    user: UserProfileDto;
    selectionType: FeedAuthorSelectionType;
    isFiltered: boolean;
    onFilterChange: (filter: IFeedFilterOptions, selectionType: FeedAuthorSelectionType) => void;
    onSelectedExpandedAdvFilter: (selected: boolean) => void;
    onError: (error: any) => void;
}

/**
 * Filtro para o feed de perfil do usuário.
 * @param props
 */
export const FeedUserProfileFilter: React.FC<IProps> = (props) => {

    const { user, expandedAdvFilter, count, selectionType, isFiltered, filter, onFilterChange, onSelectedExpandedAdvFilter, onError } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const [stickers, setStickers] = useState<IAdvFilterCommand[]>([]);
    const [refreshQuickFilter, setRefreshQuickFilter] = useState<boolean>(false);

    const [order, setOrder] = useState<KnowledgeFeedOrder>(filter.order);
    const [dropdownOptions, setDropdownOptions] = useState<IAdvFilterDropdown[]>(getDropdownOptions(t, stickers, false));

    const orderOptions = useRef<IAdvFilterMenuItem<KnowledgeFeedOrder>[]>(orderMenuOptions(t));
    const searchRef = useRef<MprcSearchTextFieldHandle | null>(null);

    /// Ao carregar o menu de rótulos
    const getStickers = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            api.v1.selectors.stickerAdvFilter({ applyTo: StickerAppliesTo.Publication })
                .then(result => {
                    const filterCommands: IAdvFilterCommand[] = result.data?.map(sticker => {
                        const cmd: IAdvFilterCommand = {
                            command: CoreFilterCommand.KnowledgeSticker,
                            displayName: sticker.name,
                            canRepeat: true,
                            group: 1,
                            fixedValue: sticker.stickerId,
                            quickFilterType: KnowledgeQuickFilterType.CustomSticker,
                            canCheck: true
                        };
                        return cmd;
                    });
                    setStickers(filterCommands);
                })
                .catch(error => {
                    if (onError) onError(error);
                })
                .finally(() => resolve(true));
        });
    };

    ///
    const handleSelectionType = (selection: FeedAuthorSelectionType) => {

        setDropdownOptions(getDropdownOptions(t, stickers, selection == FeedAuthorSelectionType.Mention));

        const filters = {
            ...getClearFilter(),
            advFilters: filter.advFilters.filter(x => selection == FeedAuthorSelectionType.Mention ||
                (x.command != CoreFilterCommand.KnowledgeAuthor
                    && x.command != CoreFilterCommand.KnowledgePublishedByYou
                    && x.command != CoreFilterCommand.KnowledgeUserFollowingAuthor
                ))
        };

        setRefreshQuickFilter(!refreshQuickFilter);

        onFilterChange(filters, selection);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setDropdownOptions(getDropdownOptions(t, stickers, selectionType == FeedAuthorSelectionType.Mention));
    }, [stickers]);

    ///
    useEffect(() => {
        onFilterChange({ ...filter, order }, selectionType);
    }, [order]);

    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    const endActions = (
        <div>
            <MprcText color="muted">
                <MprcFilterIconButton
                    filtered={expandedAdvFilter}
                    onClick={() => {
                        onSelectedExpandedAdvFilter(!expandedAdvFilter);
                        if (!expandedAdvFilter) searchRef.current?.replaceSearchText(filter.searchText);
                    }
                    }
                />
            </MprcText>

            <MprcIconButtonDropdown
                menuItemCheck
                icon={<IconSort />}
                title={t("shared:Filter.OrderBy")}
                selected={order}
                items={orderOptions.current}
                onChange={(order) => setOrder(order)}
            />
        </div>
    );

    const RenderSelection = () => {
        return (
            <Box sx={{ alignText: "center", display: "flex" }} mr={2}>
                <MprcChipChoice
                    label={t("Feed.UserProfile.PublicationChoiceText")}
                    uppercase
                    size="small"
                    selected={selectionType == FeedAuthorSelectionType.Publication}
                    onClick={() => handleSelectionType(FeedAuthorSelectionType.Publication)}
                />
                <MprcChipChoice
                    label={t("Feed.UserProfile.Action.MentionChoiceText")}
                    uppercase
                    size="small"
                    selected={selectionType == FeedAuthorSelectionType.Mention}
                    onClick={() => handleSelectionType(FeedAuthorSelectionType.Mention)}
                />
            </Box>
        );
    };

    const RenderCounters = () => {
        return (count ?? 0) > 0 &&
            <Box mt={1}>
                <MprcFilterCountInfo
                    variant={isFiltered ? "bodyNotice" : "caption"}
                    text={
                        t(
                            isFiltered
                                ? "Feed.UserProfile.FoundPublicationMessage"
                                : selectionType == FeedAuthorSelectionType.Publication
                                    ? "Feed.UserProfile.PublicationMessage"
                                    : "Feed.UserProfile.MentionMessage", { count: count! })
                    }
                    onResetFilter={isFiltered ? () => onFilterChange(getClearFilter(), selectionType) : undefined}
                />
            </Box>;
    };


    return (
        <Box id={FEED_EXTERNAL_FILTER_ID}>
            <QuickFilter
                feedType={FeedType.UserProfile}
                selectedCommands={filter.advFilters}
                reload={refreshQuickFilter}
                onChange={(selected, quickFilter) => onFilterChange(changeQuickFilter(selected, quickFilter, dropdownOptions, filter), selectionType)}
                onGetOptions={() =>
                    new Promise<QuickFilterDto[]>((resolve, reject) => {
                        api.v1.knowledgeFeed.getQuickFilterFeedUser({ userId: user.userId, isPublication: selectionType == FeedAuthorSelectionType.Publication })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
                onError={(error => onError(error))}
            />
            <Box marginTop={2}>
                {
                    expandedAdvFilter
                        ? <MprcFilterBox>
                            <AdvancedFilter
                                filter={filter}
                                leftDropdowns={dropdownOptions}
                                startComponent={RenderSelection()}
                                endComponent={endActions}
                                onGetStickers={getStickers}
                                quickFilter={{
                                    command: CoreFilterCommand.KnowledgeQuickFilter,
                                    onRemoveQuickFilter: (item) => onFilterChange(clearAdvFilter(item, filter), selectionType)
                                }}
                                onFilterChange={
                                    (filters) => onFilterChange({ ...filters }, selectionType)
                                }
                                onError={error => onError(error)}
                            />
                        </MprcFilterBox>
                        :
                        <MprcFilterBox>
                            <MprcFilterLine>
                                <div>
                                    {RenderSelection()}
                                </div>
                                {endActions}
                            </MprcFilterLine>

                            <Grid container>
                                <Grid size="grow">
                                    <MprcSearchTextField
                                        ref={searchRef}
                                        initialValue={filter.searchText ?? ""}
                                        placeholder={t("shared:Search.TextPlaceholder")}
                                        onConfirm={(value) => onFilterChange({ ...filter, searchText: value }, selectionType)}
                                    />
                                </Grid>
                            </Grid>
                        </MprcFilterBox>

                }
            </Box>
            {RenderCounters()}
        </Box>
    );
};
